if (!customElements.get('dieux-hero-slideshow')) {
  /* Shortest time a control stays painted as pressed. A tap can be over in well
     under this, and the press is the only feedback a touchscreen gets. */
  const CONTROL_PRESS_MINIMUM = 160;
  /* Widest angle off horizontal a drag may travel and still be read as a swipe
     rather than a page scroll. */
  const SWIPE_MAXIMUM_ANGLE = 45;
  const SWIPE_AXIS_DISTANCE = 4;
  const SWIPE_MINIMUM_DISTANCE = 48;
  const SEMANTIC_ATTRIBUTES = ['role', 'aria-roledescription', 'aria-label'];

  /** Writes an attribute only when its value differs, so repeated syncs cause no mutations. */
  const setAttributeIfChanged = (element, name, value) => {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  class DieuxHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.mutationObserver?.disconnect();
      this.clearTimer();
      this.cancelTransition();
      this.clearSwipeClickSuppression();
      this.swipeGesture = undefined;

      this.abortController = new AbortController();
      this.designMode = this.dataset.designMode === 'true';
      if (this.resolveAbCandidates()) {
        this.updateSlideLabels(Array.from(this.querySelectorAll('[data-hero-slide]')));
      }

      this.track = this.querySelector('[data-hero-ref="track"]');
      this.controls = this.querySelector('[data-hero-ref="controls"]');
      this.status = this.querySelector('[data-hero-ref="status"]');
      this.toggleButton = this.querySelector('[data-hero-action="toggle"]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.pauseOnInteraction = this.dataset.pauseOnInteraction === 'true';
      this.interval = Number.parseInt(this.dataset.interval, 10) || 2000;
      this.transitionDuration = this.getTransitionDuration();
      this.motionPreference = (window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)'));
      this.hoverQuery = window.matchMedia('(hover: hover)');
      this.manuallyPaused = false;
      this.motionPreferencePaused = this.autoplay && this.motionPreference.matches;
      this.pointerSuspended = false;
      this.regionPressed = false;
      this.focusSuspended = false;
      this.editorLocked = false;
      this.swipeSuspended = false;
      this.transitioning = false;
      this.pendingTransition = undefined;

      this.awaitingPresence = this.autoplay && !this.designMode;

      const signal = this.abortController.signal;
      this.addEventListener('click', this.handleClick, { signal });
      this.addEventListener('pointerdown', this.handleControlPointerDown, { signal, passive: true });
      this.addEventListener('pointerenter', this.handlePointerEnter, { signal });
      this.addEventListener('pointerleave', this.handlePointerLeave, { signal });
      this.addEventListener('pointerover', this.handleRegionPointerCross, { signal, passive: true });
      this.addEventListener('pointerout', this.handleRegionPointerCross, { signal, passive: true });
      this.track?.addEventListener('click', this.handleTrackClickCapture, { signal, capture: true });
      this.track?.addEventListener('pointerdown', this.handleSwipePointerDown, { signal, passive: true });
      this.track?.addEventListener('touchmove', this.handleSwipeTouchMove, { signal, passive: false });
      document.addEventListener('pointerup', this.handleControlPointerUp, { signal, passive: true });
      document.addEventListener('pointercancel', this.handleControlPointerCancel, { signal, passive: true });
      document.addEventListener('pointerup', this.handleSwipePointerUp, { signal, passive: true });
      document.addEventListener('pointercancel', this.handleSwipePointerCancel, { signal, passive: true });
      this.addEventListener('focusin', this.handleFocusIn, { signal });
      this.addEventListener('focusout', this.handleFocusOut, { signal });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
      document.addEventListener('shopify:block:select', this.handleEditorSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.handleEditorDeselect, { signal });
      this.motionPreference.addEventListener('change', this.handleMotionPreferenceChange, { signal });

      if (this.track) {
        this.mutationObserver = new MutationObserver(this.handleSlidesChanged);
        this.mutationObserver.observe(this.track, { childList: true });
      }

      this.armPresenceGate(signal);
      this.refreshSlides({ announce: false });
    }

    armPresenceGate(signal) {
      if (!this.awaitingPresence) return;

      const release = () => {
        if (!this.awaitingPresence) return;
        this.awaitingPresence = false;
        this.scheduleNext();
      };

      const options = { signal, once: true, passive: true, capture: true };
      ['pointermove', 'pointerdown', 'keydown', 'touchstart', 'wheel', 'focusin'].forEach((type) => {
        document.addEventListener(type, release, options);
      });
      window.addEventListener('scroll', release, options);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.mutationObserver?.disconnect();
      this.clearTimer();
      this.cancelTransition();
      this.releaseControl(true);
      this.swipeGesture = undefined;
      this.swipeSuspended = false;
      this.regionPressed = false;
      this.clearSwipeClickSuppression();
      this.pauseAllVideos();
    }

    resolveAbCandidates() {
      if (this.designMode) return false;

      let removed = false;

      const groups = new Map();
      this.querySelectorAll('[data-ga4-hero-ab="1"]').forEach((candidate) => {
        const title = candidate.dataset.ga4HeroTitle || '';
        const variant = candidate.dataset.ga4HeroVariant || '';
        if (!title || !variant) return;
        if (!groups.has(title)) groups.set(title, []);
        groups.get(title).push({ element: candidate, variant });
      });

      groups.forEach((candidates, title) => {
        const cookieName = `ab_hero_${title}`;
        const storedVariant = this.readCookie(cookieName);
        let chosenIndex = candidates.findIndex((candidate) => candidate.variant === storedVariant);

        if (chosenIndex < 0) {
          chosenIndex = this.randomIndex(candidates.length);
          try {
            document.cookie = `${cookieName}=${encodeURIComponent(candidates[chosenIndex].variant)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
          } catch {}
        }

        candidates.forEach((candidate, index) => {
          if (index === chosenIndex) {
            candidate.element.dataset.heroAbSelected = 'true';
          } else {
            candidate.element.remove();
            removed = true;
          }
        });
      });

      return removed;
    }

    readCookie(name) {
      const prefix = `${name}=`;
      try {
        const match = document.cookie
          .split(';')
          .map((part) => part.trim())
          .find((part) => part.startsWith(prefix));
        return match ? decodeURIComponent(match.slice(prefix.length)) : '';
      } catch {
        return '';
      }
    }

    randomIndex(count) {
      if (count <= 1) return 0;
      if (!window.crypto?.getRandomValues) return Math.floor(Math.random() * count);

      const values = new Uint32Array(1);
      const max = Math.floor(0x100000000 / count) * count;
      do {
        window.crypto.getRandomValues(values);
      } while (values[0] >= max);
      return values[0] % count;
    }

    handleClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-hero-action]');
      if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

      const action = button.dataset.heroAction;
      if (action === 'previous') {
        this.stopAutoplayAfterInteraction();
        this.showSlide(this.currentIndex - 1);
        this.scheduleNext();
      } else if (action === 'next') {
        this.stopAutoplayAfterInteraction();
        this.showSlide(this.currentIndex + 1);
        this.scheduleNext();
      } else if (action === 'toggle') {
        const shouldPause = !this.isPaused();
        this.motionPreferencePaused = false;
        this.manuallyPaused = shouldPause;
        if (!this.isPaused() && button === document.activeElement) this.focusSuspended = false;
        this.updateToggle(button);
        this.scheduleNext();
      }
    };

    handleControlPointerDown = (event) => {
      if (!(event.target instanceof Element)) return;

      if (!this.regionPressed && this.isPauseRegion(event.target)) {
        this.regionPressed = true;
        this.scheduleNext();
      }

      const button = event.target.closest('[data-hero-action]');
      if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

      this.releaseControl(true);
      this.pressedControl = button;
      this.pressedAt = performance.now();
      button.dataset.pressed = 'true';
    };

    handleControlPointerUp = () => {
      this.releaseControl();
      this.releaseRegionPress();
    };

    handleControlPointerCancel = () => {
      this.releaseControl(true);
      this.releaseRegionPress();
    };

    handleTrackClickCapture = (event) => {
      if (!this.suppressNextTrackClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.clearSwipeClickSuppression();
    };

    handleSwipePointerDown = (event) => {
      if (event.pointerType === 'mouse' || !event.isPrimary || this.slides.length < 2) return;

      this.swipeGesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        axis: undefined,
      };
      this.swipeSuspended = true;
      this.scheduleNext();
    };

    handleSwipeTouchMove = (event) => {
      const gesture = this.swipeGesture;
      if (!gesture || gesture.axis === 'vertical' || event.touches.length > 1) return;

      if (gesture.axis === 'horizontal') {
        if (event.cancelable) event.preventDefault();
        return;
      }

      const touch = event.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      if (Math.hypot(deltaX, deltaY) < SWIPE_AXIS_DISTANCE) return;

      if (!this.isHorizontalTravel(deltaX, deltaY)) {
        gesture.axis = 'vertical';
        return;
      }

      gesture.axis = 'horizontal';
      if (event.cancelable) event.preventDefault();
    };

    handleSwipePointerUp = (event) => {
      const gesture = this.swipeGesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return;

      this.swipeGesture = undefined;
      this.swipeSuspended = false;

      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      const isHorizontalSwipe =
        gesture.axis !== 'vertical' &&
        Math.abs(deltaX) >= SWIPE_MINIMUM_DISTANCE &&
        (gesture.axis === 'horizontal' || this.isHorizontalTravel(deltaX, deltaY));

      if (isHorizontalSwipe) {
        this.suppressNextTrackClick = true;
        this.swipeClickTimer = window.setTimeout(() => this.clearSwipeClickSuppression(), 0);
        this.stopAutoplayAfterInteraction();
        this.showSlide(this.currentIndex + (deltaX < 0 ? 1 : -1));
      }

      this.scheduleNext();
    };

    handleSwipePointerCancel = (event) => {
      if (!this.swipeGesture || event.pointerId !== this.swipeGesture.pointerId) return;
      this.swipeGesture = undefined;
      this.swipeSuspended = false;
      this.scheduleNext();
    };

    isHorizontalTravel(deltaX, deltaY) {
      const angle = (Math.atan2(Math.abs(deltaY), Math.abs(deltaX)) * 180) / Math.PI;
      return angle <= SWIPE_MAXIMUM_ANGLE;
    }

    handlePointerEnter = (event) => {
      if (!this.pauseOnInteraction || event.pointerType !== 'mouse') return;
      this.pointerSuspended = true;
      this.scheduleNext();
    };

    handlePointerLeave = (event) => {
      if (event.pointerType !== 'mouse') return;
      this.pointerSuspended = false;
      this.scheduleNext();
    };

    handleRegionPointerCross = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (this.isPauseRegion(event.target) === this.isPauseRegion(event.relatedTarget)) return;
      this.scheduleNext();
    };

    handleFocusIn = (event) => {
      this.focusSuspended = this.isPausingFocus(event.target);
      this.scheduleNext();
    };

    handleFocusOut = (event) => {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
      this.focusSuspended = false;
      this.scheduleNext();
    };

    isPauseRegion(node) {
      if (!(node instanceof Element)) return false;
      const region = node.closest('[data-hero-pause-region]');
      return region instanceof Element && this.contains(region);
    }

    isHoveringPauseRegion() {
      if (!this.hoverQuery?.matches) return false;
      return Boolean(this.querySelector('[data-hero-pause-region]:hover'));
    }

    isPausingFocus(node) {
      if (!(node instanceof Element)) return false;
      if (this.track?.contains(node)) return true;
      return this.isPauseRegion(node) && node.matches(':focus-visible');
    }

    releaseRegionPress() {
      if (!this.regionPressed) return;
      this.regionPressed = false;
      this.scheduleNext();
    }

    handleVisibilityChange = () => {
      this.syncVideos();
      this.scheduleNext();
    };

    handleMotionPreferenceChange = () => {
      this.motionPreferencePaused = this.autoplay && this.motionPreference.matches;
      if (this.toggleButton instanceof HTMLButtonElement) this.updateToggle(this.toggleButton);
      if (this.transitioning) {
        const currentIndex = this.currentIndex;
        this.cancelTransition();
        this.commitSlide(currentIndex, false);
      }
      this.syncVideos();
      this.scheduleNext();
    };

    handleTransitionEnd = (event) => {
      if (
        !this.transitioning ||
        event.target !== this.transitionTarget ||
        event.propertyName !== 'opacity'
      ) {
        return;
      }
      this.finishTransition();
    };

    handleSlidesChanged = () => {
      this.refreshSlides({ announce: false });
    };

    handleEditorSelect = (event) => {
      if (!(event.target instanceof Element)) return;
      const slide = event.target.closest('[data-hero-slide]');
      if (!slide || !this.contains(slide)) return;
      this.selectBlock(slide.dataset.heroBlockId);
    };

    handleEditorDeselect = (event) => {
      if (!(event.target instanceof Element)) return;
      const slide = event.target.closest('[data-hero-slide]');
      if (!slide || !this.contains(slide)) return;
      this.releaseEditorSelection(slide.dataset.heroBlockId);
    };

    refreshSlides({ announce = false } = {}) {
      const activeBlockId = this.slides?.[this.currentIndex]?.dataset.heroBlockId;
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      const retainedIndex = activeBlockId
        ? this.slides.findIndex((slide) => slide.dataset.heroBlockId === activeBlockId)
        : this.slides.findIndex((slide) => slide.dataset.current === 'true');
      this.currentIndex = Math.max(0, retainedIndex);
      this.dataset.multiple = String(this.slides.length > 1);

      if (this.controls instanceof HTMLElement) this.controls.hidden = this.slides.length < 2;
      if (this.toggleButton instanceof HTMLButtonElement) {
        this.toggleButton.hidden = !this.autoplay || this.slides.length < 2;
        this.updateToggle(this.toggleButton);
      }

      if (!this.slides.length) {
        this.hidden = true;
        this.dataset.state = 'static';
        this.publishCurrentSlideId();
        return;
      }

      this.hidden = false;
      this.updateSlideLabels();
      this.commitSlide(this.currentIndex, announce);
      this.scheduleNext();
    }

    updateSlideLabels(slides = this.slides) {
      if (!slides?.length) return;
      const isCarousel = slides.length > 1;
      this.updateCarouselSemantics(isCarousel);

      const template = this.dataset.slideStatusTemplate || 'Slide __index__ of __length__';
      slides.forEach((slide, index) => {
        if (!(slide instanceof HTMLElement)) return;
        if (!isCarousel) {
          SEMANTIC_ATTRIBUTES.forEach((name) => slide.removeAttribute(name));
          return;
        }
        const status = template
          .replace('__index__', String(index + 1))
          .replace('__length__', String(slides.length));
        const label = slide.dataset.slideLabel;
        setAttributeIfChanged(slide, 'role', 'group');
        setAttributeIfChanged(slide, 'aria-roledescription', 'slide');
        setAttributeIfChanged(slide, 'aria-label', label ? `${label}, ${status}` : status);
      });
    }

    updateCarouselSemantics(isCarousel) {
      if (!isCarousel) {
        SEMANTIC_ATTRIBUTES.forEach((name) => this.removeAttribute(name));
        return;
      }
      const label = this.dataset.carouselLabel || this.getAttribute('aria-label');
      if (!label) return;
      setAttributeIfChanged(this, 'role', 'region');
      setAttributeIfChanged(this, 'aria-roledescription', 'carousel');
      setAttributeIfChanged(this, 'aria-label', label);
    }

    getTransitionDuration() {
      const value = window
        .getComputedStyle(this)
        .getPropertyValue('--hero-transition-duration')
        .trim();
      const duration = Number.parseFloat(value);
      if (!Number.isFinite(duration)) return 300;
      return value.endsWith('s') && !value.endsWith('ms') ? duration * 1000 : duration;
    }

    showSlide(index, announce = true, animate = true) {
      if (!this.slides.length) return;
      const nextIndex = (index + this.slides.length) % this.slides.length;

      if (this.transitioning) {
        if (!animate || this.motionPreference.matches) {
          this.cancelTransition();
          this.commitSlide(nextIndex, announce);
          return;
        }
        this.pendingTransition = { index: nextIndex, announce };
        return;
      }

      if (
        !animate ||
        nextIndex === this.currentIndex ||
        this.transitionDuration <= 0 ||
        this.motionPreference.matches
      ) {
        this.commitSlide(nextIndex, announce);
        return;
      }

      this.startTransition(nextIndex, announce);
    }

    startTransition(nextIndex, announce) {
      const outgoingSlide = this.slides[this.currentIndex];
      const incomingSlide = this.slides[nextIndex];
      if (!outgoingSlide || !incomingSlide) {
        this.commitSlide(nextIndex, announce);
        return;
      }

      this.transitioning = true;
      this.dataset.transitioning = 'true';
      this.transitionTarget = incomingSlide.querySelector('[data-hero-media]');
      this.transitionTarget?.addEventListener('transitionend', this.handleTransitionEnd);
      incomingSlide.dataset.transitionState = 'pre-enter';
      void incomingSlide.offsetWidth;

      outgoingSlide.dataset.current = 'false';
      outgoingSlide.dataset.transitionState = 'exiting';
      outgoingSlide.setAttribute('aria-hidden', 'true');
      outgoingSlide.inert = true;

      incomingSlide.dataset.current = 'true';
      incomingSlide.dataset.transitionState = 'entering';
      incomingSlide.setAttribute('aria-hidden', 'false');
      incomingSlide.inert = false;
      this.currentIndex = nextIndex;
      this.publishCurrentSlideId();
      this.updateCurrentSlide(announce);
      this.syncVideos();

      this.transitionTimer = window.setTimeout(
        () => this.finishTransition(),
        this.transitionDuration + 100
      );
    }

    finishTransition() {
      if (!this.transitioning) return;
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = undefined;
      this.transitionTarget?.removeEventListener('transitionend', this.handleTransitionEnd);
      this.transitionTarget = undefined;
      this.slides.forEach((slide) => delete slide.dataset.transitionState);
      this.transitioning = false;
      delete this.dataset.transitioning;
      this.notifyCurrentView();

      const pendingTransition = this.pendingTransition;
      this.pendingTransition = undefined;
      if (pendingTransition && pendingTransition.index !== this.currentIndex) {
        this.showSlide(pendingTransition.index, pendingTransition.announce);
        return;
      }

      this.scheduleNext();
    }

    cancelTransition() {
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = undefined;
      this.transitionTarget?.removeEventListener('transitionend', this.handleTransitionEnd);
      this.transitionTarget = undefined;
      this.slides?.forEach((slide) => delete slide.dataset.transitionState);
      this.transitioning = false;
      this.pendingTransition = undefined;
      delete this.dataset.transitioning;
    }

    commitSlide(index, announce) {
      if (!this.slides.length) return;
      this.currentIndex = (index + this.slides.length) % this.slides.length;
      this.dataset.transitionDisabled = 'true';

      this.slides.forEach((slide, slideIndex) => {
        const isCurrent = slideIndex === this.currentIndex;
        delete slide.dataset.transitionState;
        slide.dataset.current = String(isCurrent);
        slide.setAttribute('aria-hidden', String(!isCurrent));
        slide.inert = !isCurrent;
      });

      this.publishCurrentSlideId();

      void this.offsetWidth;
      delete this.dataset.transitionDisabled;
      this.updateCurrentSlide(announce);
      this.syncVideos();
      this.notifyCurrentView();
    }

    publishCurrentSlideId() {
      const blockId = this.slides?.[this.currentIndex]?.dataset.heroBlockId;
      if (blockId) this.dataset.currentSlide = blockId;
      else delete this.dataset.currentSlide;
    }

    updateCurrentSlide(announce) {
      const currentSlide = this.slides[this.currentIndex];
      if (!currentSlide) return;
      this.dataset.activePosition = currentSlide.dataset.contentPosition;

      if (announce && this.status) {
        this.status.textContent = currentSlide.getAttribute('aria-label') || '';
      }
    }

    notifyCurrentView() {
      const currentSlide = this.slides[this.currentIndex];
      if (!currentSlide) return;
      document.dispatchEvent(
        new CustomEvent('dieux:hero-viewed', { detail: { hero: currentSlide } })
      );
    }

    syncVideos() {
      const canPlay = !this.motionPreference.matches && !document.hidden;
      this.slides?.forEach((slide, index) => {
        slide.querySelectorAll('[data-hero-media] video').forEach((video) => {
          if (index === this.currentIndex && canPlay) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      });
    }

    pauseAllVideos() {
      this.querySelectorAll('[data-hero-media] video').forEach((video) => video.pause());
    }

    selectBlock(blockId) {
      const nextIndex = this.slides.findIndex((slide) => slide.dataset.heroBlockId === blockId);
      if (nextIndex < 0) return;
      this.editorBlockId = blockId;
      this.editorLocked = true;
      this.showSlide(nextIndex, false, false);
      this.scheduleNext();
    }

    releaseEditorSelection(blockId) {
      if (this.editorBlockId && blockId && this.editorBlockId !== blockId) return;
      this.editorBlockId = undefined;
      this.editorLocked = false;
      this.scheduleNext();
    }

    updateToggle(button) {
      const paused = this.isPaused();
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute(
        'aria-label',
        paused ? button.dataset.labelPlay : button.dataset.labelPause
      );
    }

    isPaused() {
      return this.manuallyPaused || this.motionPreferencePaused;
    }

    stopAutoplayAfterInteraction() {
      if (!this.autoplay || !this.pauseOnInteraction) return;
      this.manuallyPaused = true;
      if (this.toggleButton instanceof HTMLButtonElement) this.updateToggle(this.toggleButton);
    }

    isSuspended() {
      return (
        this.awaitingPresence ||
        this.pointerSuspended ||
        this.regionPressed ||
        this.focusSuspended ||
        this.editorLocked ||
        this.swipeSuspended ||
        document.hidden ||
        this.isHoveringPauseRegion()
      );
    }

    scheduleNext() {
      this.clearTimer();
      const suspended = this.isSuspended();
      const paused = this.isPaused();

      if (this.transitioning) {
        this.dataset.state = paused ? 'paused' : suspended ? 'suspended' : 'running';
        return;
      }

      if (this.slides.length < 2) {
        this.dataset.state = 'static';
        return;
      }

      if (!this.autoplay || paused || suspended) {
        this.dataset.state = paused ? 'paused' : suspended ? 'suspended' : 'manual';
        return;
      }

      this.dataset.state = 'running';
      const transitionDuration = this.motionPreference.matches ? 0 : this.transitionDuration;
      const autoplayDelay = Math.max(0, this.interval - transitionDuration);
      this.timer = window.setTimeout(() => {
        if (this.isSuspended()) {
          this.scheduleNext();
          return;
        }
        this.showSlide(this.currentIndex + 1, false);
        if (!this.transitioning) this.scheduleNext();
      }, autoplayDelay);
    }

    releaseControl(immediate = false) {
      window.clearTimeout(this.pressTimer);
      this.pressTimer = undefined;

      const button = this.pressedControl;
      if (!button) return;
      this.pressedControl = undefined;

      const remaining = CONTROL_PRESS_MINIMUM - (performance.now() - this.pressedAt);
      if (immediate || remaining <= 0) {
        delete button.dataset.pressed;
        return;
      }

      this.pressTimer = window.setTimeout(() => {
        delete button.dataset.pressed;
        this.pressTimer = undefined;
      }, remaining);
    }

    clearSwipeClickSuppression() {
      window.clearTimeout(this.swipeClickTimer);
      this.swipeClickTimer = undefined;
      this.suppressNextTrackClick = false;
    }

    clearTimer() {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  customElements.define('dieux-hero-slideshow', DieuxHeroSlideshow);
}
